import React, { Component } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Button, Spinner, Tabs, Tab, Modal, Form, ButtonGroup, Pagination } from 'react-bootstrap';
import { getJob, submitFeedback, getJobPreview, getSuggestedFix, fixAnomaly } from '../services/api';

class JobDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      job: null,
      preview: null,
      loading: true,
      previewLoading: false,
      error: null,
      activeTab: 'summary',
      currentPage: 1,
      perPage: 50,
      // Edit modal state
      showEditModal: false,
      selectedAnomaly: null,
      suggestions: [],
      suggestionsLoading: false,
      editValue: '',
      fixAction: 'edit',
    };
  }

  componentDidMount() {
    this.loadJob();
  }

  loadJob = async () => {
    const { id } = this.props.match.params;
    
    try {
      const job = await getJob(id);
      this.setState({ job, loading: false });
    } catch (error) {
      this.setState({ 
        error: 'Failed to load job details',
        loading: false 
      });
    }
  };

  loadPreview = async (page = 1) => {
    const { id } = this.props.match.params;
    const { perPage } = this.state;
    
    this.setState({ previewLoading: true, currentPage: page });
    
    try {
      const previewData = await getJobPreview(id, page, perPage);
      this.setState({ preview: previewData, previewLoading: false });
    } catch (error) {
      this.setState({ 
        preview: { error: 'Failed to load preview' },
        previewLoading: false 
      });
    }
  };

  handleTabSelect = (tab) => {
    this.setState({ activeTab: tab });
    if ((tab === 'original' || tab === 'cleaned') && !this.state.preview) {
      this.loadPreview(1);
    }
  };

  handlePageChange = (page) => {
    this.loadPreview(page);
  };

  handleFeedback = async (anomalyId, isTruePositive) => {
    try {
      await submitFeedback(anomalyId, isTruePositive);
      this.props.showAlert(
        `Marked as ${isTruePositive ? 'true positive' : 'false positive'}`,
        'success'
      );
      
      // If marked as true positive, show edit modal
      if (isTruePositive) {
        this.openEditModal(anomalyId);
      } else {
        // Refresh data
        this.loadJob();
        this.loadPreview(this.state.currentPage);
      }
    } catch (error) {
      this.props.showAlert('Failed to submit feedback', 'danger');
    }
  };

  openEditModal = async (anomalyId) => {
    const { preview, job } = this.state;
    const anomalyInfo = preview?.anomaly_rows?.[Object.keys(preview.anomaly_rows).find(
      key => preview.anomaly_rows[key].id === anomalyId
    )];
    
    // Find full anomaly info from job
    const fullAnomaly = job?.anomaly_records?.find(a => a.id === anomalyId);
    
    this.setState({ 
      showEditModal: true, 
      selectedAnomaly: { ...fullAnomaly, ...anomalyInfo, id: anomalyId },
      suggestionsLoading: true,
      editValue: anomalyInfo?.value || '',
      fixAction: 'edit'
    });

    try {
      const suggestions = await getSuggestedFix(anomalyId);
      this.setState({ 
        suggestions: suggestions.suggestions || [],
        suggestionsLoading: false 
      });
    } catch (error) {
      this.setState({ suggestions: [], suggestionsLoading: false });
    }
  };

  closeEditModal = () => {
    this.setState({ 
      showEditModal: false, 
      selectedAnomaly: null,
      suggestions: [],
      editValue: '',
      fixAction: 'edit'
    });
  };

  handleFix = async () => {
    const { selectedAnomaly, fixAction, editValue } = this.state;
    
    try {
      await fixAnomaly(selectedAnomaly.id, fixAction, editValue);
      this.props.showAlert(
        fixAction === 'delete_row' ? 'Row deleted' : 
        fixAction === 'delete_value' ? 'Value cleared' : 
        'Value updated',
        'success'
      );
      this.closeEditModal();
      this.loadJob();
      this.loadPreview(this.state.currentPage);
    } catch (error) {
      this.props.showAlert('Failed to fix anomaly: ' + (error.response?.data?.error || error.message), 'danger');
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  getStatusBadge = (status) => {
    const variants = {
      'completed': 'success',
      'failed': 'danger',
      'processing': 'warning',
      'pending': 'secondary',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  renderPagination = (totalPages, currentPage) => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.First key="first" onClick={() => this.handlePageChange(1)} disabled={currentPage === 1} />,
      <Pagination.Prev key="prev" onClick={() => this.handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
    );

    if (startPage > 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => this.handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" />);
    }

    items.push(
      <Pagination.Next key="next" onClick={() => this.handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />,
      <Pagination.Last key="last" onClick={() => this.handlePageChange(totalPages)} disabled={currentPage === totalPages} />
    );

    return <Pagination className="justify-content-center mt-3">{items}</Pagination>;
  };

  renderDataTable = (data, columns, anomalyRows = {}, startIndex = 0) => {
    if (!data || data.length === 0) {
      return <p className="text-muted">No data available</p>;
    }

    return (
      <div className="table-responsive" style={{ maxHeight: '500px', overflow: 'auto' }}>
        <Table striped bordered hover size="sm">
          <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ minWidth: '120px' }}>Row # / Actions</th>
              {columns.map((col, idx) => (
                <th key={idx}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => {
              const actualRowIndex = startIndex + rowIdx;
              const anomalyInfo = anomalyRows[actualRowIndex];
              const isAnomalyRow = !!anomalyInfo;
              const hasFeedback = anomalyInfo?.feedback;
              
              return (
                <tr 
                  key={rowIdx} 
                  style={isAnomalyRow ? { 
                    backgroundColor: hasFeedback 
                      ? (hasFeedback.is_true_positive ? '#fff3cd' : '#d4edda')
                      : '#ffe6e6' 
                  } : {}}
                >
                  <td style={{ minWidth: '120px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>
                        {actualRowIndex + 1}
                        {isAnomalyRow && !hasFeedback && <span style={{ color: 'red', marginLeft: '4px' }}>⚠</span>}
                        {hasFeedback && (
                          <Badge 
                            variant={hasFeedback.is_true_positive ? 'warning' : 'success'} 
                            className="ml-1"
                            style={{ fontSize: '0.6rem' }}
                          >
                            {hasFeedback.is_true_positive ? 'TP' : 'FP'}
                          </Badge>
                        )}
                      </span>
                      {isAnomalyRow && !hasFeedback && (
                        <ButtonGroup size="sm">
                          <Button
                            variant="outline-success"
                            onClick={() => this.handleFeedback(anomalyInfo.id, true)}
                            title="True Positive - Fix this"
                            style={{ padding: '0 4px', fontSize: '0.7rem' }}
                          >
                            ✓
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => this.handleFeedback(anomalyInfo.id, false)}
                            title="False Positive - Not an issue"
                            style={{ padding: '0 4px', fontSize: '0.7rem' }}
                          >
                            ✗
                          </Button>
                        </ButtonGroup>
                      )}
                      {hasFeedback && hasFeedback.is_true_positive && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => this.openEditModal(anomalyInfo.id)}
                          title="Edit this value"
                          style={{ padding: '0 4px', fontSize: '0.7rem' }}
                        >
                          ✏️
                        </Button>
                      )}
                    </div>
                  </td>
                  {columns.map((col, colIdx) => {
                    const isAnomalyCell = isAnomalyRow && anomalyInfo.column === col;
                    return (
                      <td 
                        key={colIdx}
                        style={isAnomalyCell ? { 
                          backgroundColor: hasFeedback 
                            ? (hasFeedback.is_true_positive ? '#ffc107' : '#28a745')
                            : '#ffcccc',
                          fontWeight: 'bold',
                          border: '2px solid ' + (hasFeedback 
                            ? (hasFeedback.is_true_positive ? '#ff9800' : '#28a745')
                            : 'red')
                        } : {}}
                        title={isAnomalyCell ? `Anomaly score: ${anomalyInfo.score?.toFixed(3)}` : ''}
                      >
                        {row[col] !== null && row[col] !== undefined && row[col] !== ''
                          ? String(row[col]).substring(0, 50) 
                          : <span className="text-muted">null</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  renderEditModal = () => {
    const { showEditModal, selectedAnomaly, suggestions, suggestionsLoading, editValue, fixAction } = this.state;

    return (
      <Modal show={showEditModal} onHide={this.closeEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Fix Anomaly</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAnomaly && (
            <>
              <Table size="sm" bordered>
                <tbody>
                  <tr>
                    <td><strong>Row</strong></td>
                    <td>{(selectedAnomaly.row_index || 0) + 1}</td>
                  </tr>
                  <tr>
                    <td><strong>Column</strong></td>
                    <td>{selectedAnomaly.column || selectedAnomaly.column_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Current Value</strong></td>
                    <td><code>{selectedAnomaly.value || selectedAnomaly.original_value || 'N/A'}</code></td>
                  </tr>
                  <tr>
                    <td><strong>Anomaly Score</strong></td>
                    <td>{selectedAnomaly.score?.toFixed(3) || selectedAnomaly.anomaly_score?.toFixed(3) || 'N/A'}</td>
                  </tr>
                </tbody>
              </Table>

              <Form.Group className="mb-3">
                <Form.Label><strong>Action</strong></Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    id="action-edit"
                    label="Edit value"
                    checked={fixAction === 'edit'}
                    onChange={() => this.setState({ fixAction: 'edit' })}
                  />
                  <Form.Check
                    type="radio"
                    id="action-delete-value"
                    label="Clear value (set to null)"
                    checked={fixAction === 'delete_value'}
                    onChange={() => this.setState({ fixAction: 'delete_value' })}
                  />
                  <Form.Check
                    type="radio"
                    id="action-delete-row"
                    label="Delete entire row"
                    checked={fixAction === 'delete_row'}
                    onChange={() => this.setState({ fixAction: 'delete_row' })}
                  />
                </div>
              </Form.Group>

              {fixAction === 'edit' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>New Value</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={editValue}
                      onChange={(e) => this.setState({ editValue: e.target.value })}
                      placeholder="Enter new value"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label><strong>Suggested Values</strong></Form.Label>
                    {suggestionsLoading ? (
                      <div><Spinner animation="border" size="sm" /> Loading...</div>
                    ) : suggestions.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {suggestions.map((s, idx) => (
                          <Button
                            key={idx}
                            variant="outline-primary"
                            size="sm"
                            className="mr-2 mb-2"
                            onClick={() => this.setState({ editValue: String(s.value) })}
                          >
                            {s.label}: {s.value}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No suggestions available</p>
                    )}
                  </Form.Group>
                </>
              )}

              {fixAction === 'delete_row' && (
                <div className="alert alert-warning">
                  ⚠️ This will permanently delete row {(selectedAnomaly.row_index || 0) + 1} from the cleaned file.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.closeEditModal}>
            Cancel
          </Button>
          <Button 
            variant={fixAction === 'delete_row' ? 'danger' : 'primary'} 
            onClick={this.handleFix}
          >
            {fixAction === 'delete_row' ? 'Delete Row' : 
             fixAction === 'delete_value' ? 'Clear Value' : 
             'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  render() {
    const { job, preview, loading, previewLoading, error, activeTab, currentPage } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading job details...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h4>{error}</h4>
          <Link to="/jobs" className="btn btn-primary">Back to Jobs</Link>
        </div>
      );
    }

    if (!job) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h4>Job not found</h4>
          <Link to="/jobs" className="btn btn-primary">Back to Jobs</Link>
        </div>
      );
    }

    const totalPages = preview?.original?.total_pages || 1;
    const startIndex = preview?.original?.start_index || 0;

    return (
      <div>
        {this.renderEditModal()}
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2>Job #{job.id}: {job.filename}</h2>
            <p className="text-muted mb-0">
              Created: {this.formatDate(job.created_at)} | 
              Status: {this.getStatusBadge(job.status)}
            </p>
          </div>
          <div>
            {job.download_url && (
              <a 
                href={job.download_url}
                className="btn btn-success mr-2"
                download
              >
                ⬇ Download Cleaned File
              </a>
            )}
            <Link to="/jobs" className="btn btn-outline-secondary">
              ← Back to Jobs
            </Link>
          </div>
        </div>

        {/* Summary Stats */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{job.original_shape?.[0] || 0}</div>
              <div className="stat-label">Original Rows</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{job.cleaned_shape?.[0] || 0}</div>
              <div className="stat-label">Cleaned Rows</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{job.anomalies?.ml_count || 0}</div>
              <div className="stat-label">Anomalies</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{job.processing_time_ms || 0} ms</div>
              <div className="stat-label">Processing Time</div>
            </Card>
          </Col>
        </Row>

        {/* Legend */}
        <div className="mb-3 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
          <small>
            <strong>Legend:</strong>{' '}
            <span style={{ backgroundColor: '#ffe6e6', padding: '2px 6px', marginRight: '10px' }}>⚠ Pending review</span>
            <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px', marginRight: '10px' }}>TP = True Positive (needs fix)</span>
            <span style={{ backgroundColor: '#d4edda', padding: '2px 6px' }}>FP = False Positive (ok)</span>
          </small>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onSelect={this.handleTabSelect} className="mb-3">
          <Tab eventKey="summary" title="Summary">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>Validation Results</Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <tbody>
                        <tr>
                          <td>Is Valid</td>
                          <td>
                            <Badge variant={job.validation?.is_valid ? 'success' : 'warning'}>
                              {job.validation?.is_valid ? 'Yes' : 'No'}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td>Total Issues</td>
                          <td>{job.validation?.total_issues || 0}</td>
                        </tr>
                        <tr>
                          <td>Missing Value Columns</td>
                          <td>{job.validation?.missing_value_columns || 0}</td>
                        </tr>
                        <tr>
                          <td>Duplicate Rows</td>
                          <td>{job.validation?.duplicate_rows || 0}</td>
                        </tr>
                        <tr>
                          <td>Empty Columns</td>
                          <td>{job.validation?.empty_columns || 0}</td>
                        </tr>
                        <tr>
                          <td>Constant Columns</td>
                          <td>{job.validation?.constant_columns || 0}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header>Anomaly Detection</Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <tbody>
                        <tr>
                          <td>ML Anomalies</td>
                          <td>
                            <Badge variant="danger">{job.anomalies?.ml_count || 0}</Badge>
                            {' '}({(job.anomalies?.ml_percentage || 0).toFixed(2)}%)
                          </td>
                        </tr>
                        <tr>
                          <td>Extreme Values</td>
                          <td>{job.anomalies?.extreme_values || 0}</td>
                        </tr>
                        <tr>
                          <td>Statistical Outliers</td>
                          <td>{job.anomalies?.statistical_outliers || 0}</td>
                        </tr>
                        <tr>
                          <td>Model Version</td>
                          <td>{job.model_version_id || 'Default'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col md={12}>
                <Card>
                  <Card.Header>Cleaning Summary</Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <strong>Rows Removed:</strong> {job.rows_removed || 0}
                      </Col>
                      <Col md={3}>
                        <strong>Columns Removed:</strong> {job.columns_removed || 0}
                      </Col>
                      <Col md={3}>
                        <strong>Original Shape:</strong> {job.original_shape?.join(' × ') || 'N/A'}
                      </Col>
                      <Col md={3}>
                        <strong>Cleaned Shape:</strong> {job.cleaned_shape?.join(' × ') || 'N/A'}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="original" title="📄 Original Data">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  Original File: {job.filename}
                  {preview?.original && (
                    <span className="text-muted ml-2">
                      ({preview.original.total_rows || 0} rows, page {currentPage} of {totalPages})
                    </span>
                  )}
                </span>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => this.loadPreview(currentPage)}
                  disabled={previewLoading}
                >
                  {previewLoading ? <Spinner animation="border" size="sm" /> : '↻ Refresh'}
                </Button>
              </Card.Header>
              <Card.Body>
                {previewLoading && !preview ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ml-2">Loading preview...</span>
                  </div>
                ) : preview?.original?.error ? (
                  <p className="text-danger">{preview.original.error}</p>
                ) : preview?.original?.data ? (
                  <>
                    {this.renderDataTable(
                      preview.original.data, 
                      preview.original.columns, 
                      preview.anomaly_rows || {},
                      startIndex
                    )}
                    {this.renderPagination(totalPages, currentPage)}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Button variant="primary" onClick={() => this.loadPreview(1)}>
                      Load Preview
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="cleaned" title="✨ Cleaned Data">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  Cleaned File: cleaned_{job.id}_{job.filename}
                  {preview?.cleaned && !preview.cleaned.error && (
                    <span className="text-muted ml-2">
                      ({preview.cleaned.total_rows || 0} rows, page {currentPage} of {preview.cleaned.total_pages || 1})
                    </span>
                  )}
                </span>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => this.loadPreview(currentPage)}
                  disabled={previewLoading}
                >
                  {previewLoading ? <Spinner animation="border" size="sm" /> : '↻ Refresh'}
                </Button>
              </Card.Header>
              <Card.Body>
                {previewLoading && !preview ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ml-2">Loading preview...</span>
                  </div>
                ) : preview?.cleaned?.error ? (
                  <div className="text-warning">
                    <p>⚠️ {preview.cleaned.error}</p>
                    {preview.cleaned.expected_path && (
                      <small className="text-muted">Expected: {preview.cleaned.expected_path}</small>
                    )}
                  </div>
                ) : preview?.cleaned?.data ? (
                  <>
                    {this.renderDataTable(
                      preview.cleaned.data, 
                      preview.cleaned.columns, 
                      preview.anomaly_rows || {},
                      preview.cleaned.start_index || 0
                    )}
                    {this.renderPagination(preview.cleaned.total_pages || 1, currentPage)}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Button variant="primary" onClick={() => this.loadPreview(1)}>
                      Load Preview
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="anomalies" title={`🔍 Anomalies (${job.anomaly_records?.length || 0})`}>
            {job.anomaly_records && job.anomaly_records.length > 0 ? (
              <Card>
                <Card.Body>
                  <Table responsive hover size="sm">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Row</th>
                        <th>Column</th>
                        <th>Method</th>
                        <th>Score</th>
                        <th>Original Value</th>
                        <th>Corrected Value</th>
                        <th>Feedback</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.anomaly_records.map((anomaly) => (
                        <tr key={anomaly.id}>
                          <td>{anomaly.id}</td>
                          <td>{anomaly.row_index + 1}</td>
                          <td>{anomaly.column_name || 'N/A'}</td>
                          <td>
                            <Badge variant="info">{anomaly.detection_method}</Badge>
                          </td>
                          <td>{anomaly.anomaly_score?.toFixed(3) || 'N/A'}</td>
                          <td>
                            <code>{anomaly.original_value || 'N/A'}</code>
                          </td>
                          <td>
                            {anomaly.feedback?.corrected_value ? (
                              <code className="text-success">{anomaly.feedback.corrected_value}</code>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {anomaly.feedback ? (
                              <Badge variant={anomaly.feedback.is_true_positive ? 'warning' : 'success'}>
                                {anomaly.feedback.is_true_positive ? 'True +' : 'False +'}
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Pending</Badge>
                            )}
                          </td>
                          <td>
                            {!anomaly.feedback ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  className="mr-1"
                                  onClick={() => this.handleFeedback(anomaly.id, true)}
                                  title="True Positive"
                                >
                                  ✓ True
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => this.handleFeedback(anomaly.id, false)}
                                  title="False Positive"
                                >
                                  ✗ False
                                </Button>
                              </>
                            ) : anomaly.feedback.is_true_positive && (
                              <Button
                                size="sm"
                                variant={anomaly.feedback.corrected_value ? 'outline-warning' : 'warning'}
                                onClick={() => this.openEditModal(anomaly.id)}
                              >
                                {anomaly.feedback.corrected_value ? '✏️ Edit' : '✏️ Fix'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <h4>No anomalies detected</h4>
                <p>This file passed all anomaly detection checks.</p>
              </div>
            )}
          </Tab>
        </Tabs>
      </div>
    );
  }
}

export default withRouter(JobDetail);